import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings } from 'lucide-react';
import { useApp } from '../context/AppContext';
import PinPad from '../components/PinPad';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = ({ onEnter, onAuthenticated }) => {
  const { t } = useApp();
  const [showPinPad, setShowPinPad] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(true);

  useEffect(() => {
    checkFirstLaunch();
  }, []);

  const checkFirstLaunch = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setIsFirstTime(response.data.is_first_launch);
      if (response.data.is_first_launch) {
        setTimeout(() => setShowPinPad(true), 500);
      }
    } catch (error) {
      console.error('Error checking first launch:', error);
    }
  };

  const handlePinSuccess = async (pin) => {
    if (isFirstTime) {
      try {
        await axios.post(`${API}/settings`, {
          passcode: pin,
          language: 'en'
        });
        setIsFirstTime(false);
        setShowPinPad(false);
      } catch (error) {
        console.error('Error saving settings:', error);
      }
    } else {
      try {
        const response = await axios.post(`${API}/settings/verify-passcode`, {
          passcode: pin
        });
        if (response.data.success) {
          onAuthenticated(response.data.youtube_api_key);
          setShowPinPad(false);
        }
      } catch (error) {
        console.error('Invalid passcode');
        setShowPinPad(false);
        setTimeout(() => setShowPinPad(true), 100);
      }
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage:
          'url(https://images.unsplash.com/photo-1769700016169-a3aff2356663?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjb2xvcmZ1bCUyMGJ1aWxkaW5nJTIwYmxvY2tzJTIwdG95fGVufDB8fHx8MTc2OTg1MDcxN3ww&ixlib=rb-4.1.0&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="landing-page"
    >
      <div className="absolute inset-0 bg-[#FFFDF5]/90 backdrop-blur-md" />

      <motion.button
        onClick={() => setShowPinPad(true)}
        whileHover={{ scale: 1.05, opacity: 1 }}
        className="absolute top-6 right-6 p-3 rounded-full bg-white/50 hover:bg-white/70 opacity-30 hover:opacity-100 transition-all z-10"
        data-testid="settings-icon-button"
      >
        <Settings size={20} className="text-gray-600" />
      </motion.button>

      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 text-center"
      >
        <motion.button
          onClick={onEnter}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-800 font-bold text-6xl md:text-8xl px-20 md:px-32 py-12 md:py-16 rounded-full shadow-2xl fredoka animate-bounce hover:animate-none transition-all"
          style={{
            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.3)'
          }}
          data-testid="enter-button"
        >
          {t('enter')}
        </motion.button>
      </motion.div>

      {showPinPad && (
        <PinPad
          onSuccess={handlePinSuccess}
          onClose={() => !isFirstTime && setShowPinPad(false)}
          isFirstTime={isFirstTime}
          t={t}
        />
      )}
    </div>
  );
};

export default Landing;
